<script setup lang="ts">
import dayjs from '@/lib/dayjs'
import { currentUser } from '@/lib/currentUser'
import { getIntlState } from '@/lib/intl'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

interface SmartBookData {
  purchased?: boolean
  purchase_expiry_date?: string
  tariff?: 'b' | 'e'
}

defineProps<{
  studentId?: number
  title?: string
  image?: string | null
  href?: string
  video?: string
  description?: string
  smartBook?: SmartBookData
}>()

defineEmits<{
  (e: 'click:change-course'): void
  (e: 'click:shop'): void
  (e: 'click:view'): void
}>()

const onPurchaseClick = (event: Event) => {
  if (!getIntlState()) return
  if (currentUser.hasTeacherPurchase) return
  event.preventDefault()
}

const formatDate = (val: string) => dayjs(val).format('DD.MM.YYYY')
const formatTariff = (val?: 'b' | 'e') =>
  ({
    b: 'Стандартная версия',
    e: 'Расширенная версия'
  })[val || 'b']
</script>

<template>
  <div class="student-book d-flex">
    <img
      v-if="smartBook && smartBook.purchased"
      class="student-book__smartbook-star"
      :src="asset(`img/${smartBook.tariff === 'e' ? 'smartbook-icon' : 'smartbook-basic-icon'}.svg`)"
      width="48"
      height="48"
    />

    <a v-if="image" class="student-book__image-wrapper mr-2" :href="href" target="_blank" @click="onPurchaseClick">
      <v-img class="student-book__image" :src="image" :alt="title"></v-img>
    </a>

    <div class="student-book__content" :class="{ 'student-book__content_row': !image }">
      <div class="student-book__body">
        <h3
          class="student-book__title"
          :class="{ 'student-book__title_has-smartbook-icon': smartBook && smartBook.purchased }"
        >
          {{ title }}
        </h3>

        <v-link mode="primary" class="text-button mt-half" @click="$emit('click:change-course')"> Сменить курс </v-link>

        <div v-if="smartBook && smartBook.purchased" class="student-book__smartbook-expiry-badge">
          До {{ formatDate(smartBook.purchase_expiry_date ?? '') }}<br />
          {{ formatTariff(smartBook.tariff) }}
          <svg viewBox="0 0 12 12" width="12">
            <path d="M0 12 6 0 12 12" />
          </svg>
        </div>
      </div>

      <div class="student-book__actions">
        <v-button mode="small primary" class="mt-1" :href="href" @click="$emit('click:shop')"> Магазин </v-button>
        <v-button v-if="video" mode="small primary outlined" class="mt-1" @click="$emit('click:view')">
          <v-icon class="mr-1">mdi-video</v-icon>
          Подробнее
        </v-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.student-book {
  padding: 2 * $grid-gap;
  border-radius: 2 * $grid-gap;
  background-color: $lightenGray;

  position: relative;

  &__title {
    @include text-title;
    font-weight: bold;

    &_has-smartbook-icon {
      padding-right: 48px;
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &_row {
      flex-direction: row;
    }
  }

  &__image-wrapper,
  &__image {
    flex: 0 0 144px;
    height: 144px;

    @include mobile {
      flex: 0 0 72px;
      height: 72px;
    }
  }

  &__image {
    border-radius: 8px;
  }

  &__actions,
  &__body {
    display: flex;
    flex-direction: column;
  }

  &__smartbook-star {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  &__smartbook-expiry-badge {
    @include text-caption;
    margin-top: 12px;
    padding: 4px;
    border-radius: 4px;
    background: $lightenBlue;
    border: 1px solid $lightBlue;
    position: relative;

    & > svg {
      fill: $lightenBlue;
      stroke: $lightBlue;
      position: absolute;
      right: 16px;
      top: -12px;
    }
  }
}
</style>
